'use client';

import React, { useEffect, useState } from 'react';

const IS_DEV = process.env.NODE_ENV !== 'production';

const PRIMARY = 'var(--color-primary, #006300)';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    console.error(
      '[global-error boundary]',
      error.digest ?? error.message,
      error
    );
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          background: '#fff',
          color: '#1B1B1B',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          textAlign: 'center',
          padding: '0 24px',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            border: '2px solid #DC2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <span
            style={{
              fontSize: 28,
              color: '#DC2626',
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            !
          </span>
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 12px' }}>
          Something went wrong
        </h1>
        <p
          style={{
            color: '#5A5E67',
            maxWidth: 420,
            lineHeight: 1.7,
            margin: '0 0 8px',
          }}
        >
          We encountered an unexpected error. Please try again. If the issue
          persists, contact support.
        </p>
        {error.digest && (
          <p
            style={{
              color: '#9CA3AF',
              fontSize: 12,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              margin: '0 0 24px',
            }}
          >
            Reference: {error.digest}
          </p>
        )}
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginTop: error.digest ? 0 : 24,
          }}
        >
          <button
            type="button"
            onClick={reset}
            style={{
              padding: '12px 28px',
              background: PRIMARY,
              color: '#fff',
              border: `1px solid ${PRIMARY}`,
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 15,
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.assign('/')}
            style={{
              padding: '12px 28px',
              background: 'transparent',
              border: `1px solid ${PRIMARY}`,
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 15,
              color: PRIMARY,
            }}
          >
            Go home
          </button>
        </div>

        {IS_DEV && (
          <div
            style={{
              marginTop: 32,
              width: '100%',
              maxWidth: 640,
              textAlign: 'left',
            }}
          >
            <button
              type="button"
              onClick={() => setDetailsOpen((o) => !o)}
              aria-expanded={detailsOpen}
              style={{
                background: 'none',
                border: 'none',
                color: '#444',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}
            >
              {detailsOpen
                ? '▲ Hide error details'
                : '▼ Show error details (dev only)'}
            </button>
            {detailsOpen && (
              <div
                style={{
                  marginTop: 12,
                  border: '1px solid #E9EAEB',
                  background: '#FAFAFA',
                  padding: 16,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 320,
                  lineHeight: 1.6,
                }}
              >
                <div style={{ marginBottom: 8 }}>
                  <strong>message:</strong> {error.message || '(no message)'}
                </div>
                {error.digest && (
                  <div style={{ marginBottom: 8 }}>
                    <strong>digest:</strong> {error.digest}
                  </div>
                )}
                {error.stack && (
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0,
                      opacity: 0.8,
                    }}
                  >
                    {error.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </body>
    </html>
  );
}
